import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trash2, User, Calendar, Package, Edit2, MessageSquare, Plus, Minus } from 'lucide-react';
import { useToast } from './Toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Color mapping for different tags
const getTagColor = (tag) => {
  const colors = {
    'MEDLEM': 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
    'SIKKERH.KURS': 'bg-red-500/20 text-red-400 border border-red-500/30',
    'PRØVESKYTING': 'bg-purple-500/20 text-purple-400 border border-purple-500/30',
    'INNSKYTING': 'bg-green-500/20 text-green-400 border border-green-500/30',
    'STEVNE': 'bg-orange-500/20 text-orange-400 border border-orange-500/30',
    'KURS': 'bg-pink-500/20 text-pink-400 border border-pink-500/30',
    'TRENING': 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30',
    'ØVELSE': 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30',
  };
  
  // Return color if found, otherwise use yellow as default
  return colors[tag] || 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30';
};

// Helper function to check if a sale is non-revenue (internal activity)
const isNonRevenue = (saleTags, nonRevenueTags) => {
  if (!saleTags || !Array.isArray(saleTags)) {
    return false;
  }
  return saleTags.some(tag => nonRevenueTags.includes(tag));
};

const AmmoSalesList = ({ sessions, onOpenAmmoModal }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState([]);
  const [searchTerm, setSearchTerm] = useState(localStorage.getItem('ammoSalesSearchTerm') || '');
  const [selectedTagFilter, setSelectedTagFilter] = useState(localStorage.getItem('ammoSalesTagFilter') || 'ALLE');
  const [availableTags, setAvailableTags] = useState(['MEDLEM', 'SIKKERH.KURS', 'PRØVESKYTING', 'INNSKYTING']);
  const [nonRevenueTags, setNonRevenueTags] = useState(['INNSKYTING']); // Tags that don't count as revenue
  const [editingSale, setEditingSale] = useState(null);
  const [editForm, setEditForm] = useState({ boxes_sold: 0, tags: ['MEDLEM'], comment: '' });
  const [hoveredComment, setHoveredComment] = useState(null); // For showing comment popup
  const [newCategoryInput, setNewCategoryInput] = useState(''); // For adding new categories
  const [savingNewCategory, setSavingNewCategory] = useState(false);
  
  // Date filter states
  const [dateFilter, setDateFilter] = useState('siste_aktivitet'); // Default to last activity
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false); // Collapsible filter state
  const [lastActivityDate, setLastActivityDate] = useState(null); // Last training activity date
  
  // Annual report states
  const [showAnnualReportModal, setShowAnnualReportModal] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear() - 1); // Default to last year
  const [generatingAnnualReport, setGeneratingAnnualReport] = useState(false);
  
  // Audit trail states
  const [showDeletionLog, setShowDeletionLog] = useState(false);
  const [deletedSales, setDeletedSales] = useState([]);
  const [loadingDeletedSales, setLoadingDeletedSales] = useState(false);

  useEffect(() => {
    fetchTags();
    fetchSales();
    findLastActivityDate();

    // Listen for filter changes from AmmoSalesHeader
    const handleStorageChange = () => {
      setSearchTerm(localStorage.getItem('ammoSalesSearchTerm') || '');
      setSelectedTagFilter(localStorage.getItem('ammoSalesTagFilter') || 'ALLE');
    };

    // Poll for changes every 100ms (since storage event doesn't fire in same tab)
    const interval = setInterval(handleStorageChange, 100);
    return () => clearInterval(interval);
  }, []);

  // Find last date with training activity
  const findLastActivityDate = () => {
    if (sessions && sessions.length > 0) {
      // Sort sessions by start_time descending and find the most recent one
      const sortedSessions = [...sessions].sort((a, b) => {
        const dateA = new Date(a.start_time);
        const dateB = new Date(b.start_time);
        return dateB - dateA;
      });
      
      if (sortedSessions[0]?.start_time) {
        const lastDate = new Date(sortedSessions[0].start_time);
        setLastActivityDate(lastDate);
      }
    }
  };

  // Re-find last activity date when sessions change
  useEffect(() => {
    findLastActivityDate();
  }, [sessions]);

  const fetchTags = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/admin/ammo-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const tags = response.data.ammo_tags || ['MEDLEM', 'SIKKERH.KURS', 'PRØVESKYTING', 'INNSKYTING'];
      const nonRevenue = response.data.non_revenue_tags || ['INNSKYTING'];
      setAvailableTags(tags);
      setNonRevenueTags(nonRevenue);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const handleAddNewCategory = async () => {
    const category = newCategoryInput.trim().toUpperCase();
    
    if (!category) {
      toast.error('Skriv inn en kategori');
      return;
    }
    
    // Check if category already exists
    if (availableTags.includes(category)) {
      toast.error('Denne kategorien finnes allerede');
      return;
    }
    
    try {
      setSavingNewCategory(true);
      const token = localStorage.getItem('token');
      
      // Get current settings
      const settingsResponse = await axios.get(`${API_BASE}/api/admin/ammo-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const currentSettings = settingsResponse.data;
      const customTags = currentSettings.custom_ammo_tags || [];
      
      // Add new category to custom_ammo_tags
      const updatedSettings = {
        ...currentSettings,
        custom_ammo_tags: [...customTags, category]
      };
      
      // Save to backend
      await axios.put(
        `${API_BASE}/api/admin/ammo-settings`,
        updatedSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      await fetchTags(); // Re-fetch to get updated tags
      setNewCategoryInput('');
      
      // Automatically select the new category in editForm
      setEditForm({ ...editForm, tags: [...editForm.tags, category] });
      
      toast.success(`Kategori "${category}" lagt til og lagret`);
    } catch (error) {
      console.error('Error adding new category:', error);
      toast.error('Kunne ikke legge til kategori');
    } finally {
      setSavingNewCategory(false);
    }
  };

  const loadDeletedSales = async () => {
    setLoadingDeletedSales(true);
    try {
      const token = localStorage.getItem('token');
      // Use new history endpoint that includes both deleted sales and edited sales
      const response = await axios.get(`${API_BASE}/api/admin/ammo-sales/history?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeletedSales(response.data);
    } catch (error) {
      console.error('Failed to load history:', error);
      toast.error('Kunne ikke hente historikk.');
    } finally {
      setLoadingDeletedSales(false);
    }
  };

  const restoreSale = async (saleId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/api/admin/ammo-sale/${saleId}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Salg gjenopprettet!');
      
      // Reload both active sales and history
      await fetchSales();
      if (showDeletionLog) {
        await loadDeletedSales();
      }
      
    } catch (error) {
      console.error('Failed to restore sale:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Kunne ikke gjenopprette salget. Prøv igjen.');
      }
    }
  };

  const fetchSales = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/admin/ammo-sales?limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSales(response.data.sales || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching ammo sales:', error);
      toast.error('Kunne ikke laste ammo-salg');
      setLoading(false);
    }
  };

  const handleDelete = async (saleId) => {
    if (!window.confirm('Er du sikker på at du vil slette dette salget?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/api/admin/ammo-sale/${saleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Salg slettet');
      await fetchSales();
      
      // Auto-refresh history if it's open
      if (showDeletionLog) {
        await loadDeletedSales();
      }
    } catch (error) {
      console.error('Error deleting sale:', error);
      toast.error('Kunne ikke slette salg');
    }
  };

  const handleEditClick = (sale) => {
    setEditingSale(sale);
    // Extract date from sold_at timestamp
    const saleDate = sale.sold_at ? new Date(sale.sold_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    setEditForm({
      boxes_sold: sale.boxes_sold,
      tags: sale.tags && sale.tags.length > 0 ? sale.tags : ['MEDLEM'],
      comment: sale.comment || '',
      sale_date: saleDate
    });
  };

  const handleUpdateSale = async () => {
    if (editForm.boxes_sold <= 0) {
      toast.error('Antall esker må være større enn 0');
      return;
    }
    
    if (!editForm.tags || editForm.tags.length === 0) {
      toast.error('Velg minst én kategori');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE}/api/admin/ammo-sale/${editingSale.id}`,
        { 
          boxes_sold: editForm.boxes_sold,
          tags: editForm.tags,
          comment: editForm.comment,
          sale_date: editForm.sale_date
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Salg oppdatert');
      setEditingSale(null);
      await fetchSales();
      
      // Auto-refresh history if it's open
      if (showDeletionLog) {
        await loadDeletedSales();
      }
    } catch (error) {
      console.error('Error updating sale:', error);
      toast.error('Kunne ikke oppdatere salg');
    }
  };

  // Calculate date range based on filter
  const getDateRange = () => {
    const now = new Date();
    let startDate = null;
    let endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    
    switch (dateFilter) {
      case 'siste_aktivitet':
        // Use last training activity date
        if (lastActivityDate) {
          startDate = new Date(lastActivityDate.getFullYear(), lastActivityDate.getMonth(), lastActivityDate.getDate(), 0, 0, 0);
          endDate = new Date(lastActivityDate.getFullYear(), lastActivityDate.getMonth(), lastActivityDate.getDate(), 23, 59, 59);
        } else {
          // Fallback to today if no activity found
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        }
        break;
      case 'idag':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        break;
      case '1uke':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        break;
      case '1mnd':
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case '6mnd':
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 6);
        startDate.setHours(0, 0, 0, 0);
        break;
      case '12mnd':
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'egendefinert':
        if (customStartDate) {
          startDate = new Date(customStartDate);
          startDate.setHours(0, 0, 0, 0);
        }
        if (customEndDate) {
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
        }
        break;
      default:
        // 'alle' - no date filter
        break;
    }
    
    return { startDate, endDate: dateFilter === 'alle' ? null : endDate };
  };

  const filteredSales = sales.filter(sale => {
    // Search filter
    const matchesSearch = sale.member_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.caliber?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Tag filter
    const matchesTag = selectedTagFilter === 'ALLE' || 
      (sale.tags && sale.tags.includes(selectedTagFilter));
    
    // Date filter
    let matchesDate = true;
    if (dateFilter !== 'alle') {
      const { startDate, endDate } = getDateRange();
      const saleDate = new Date(sale.sold_at);
      
      if (startDate && endDate) {
        matchesDate = saleDate >= startDate && saleDate <= endDate;
      } else if (startDate) {
        matchesDate = saleDate >= startDate;
      } else if (endDate) {
        matchesDate = saleDate <= endDate;
      }
    }
    
    return matchesSearch && matchesTag && matchesDate;
  });

  // Group by date
  const salesByDate = filteredSales.reduce((acc, sale) => {
    const date = new Date(sale.sold_at).toLocaleDateString('nb-NO');
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(sale);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">Laster ammo-salg...</p>
      </div>
    );
  }

  const handleRegisterSale = () => {
    // Open ammo modal for direct sale (not linked to a session)
    // Pass null for session to indicate this is a standalone sale
    onOpenAmmoModal(null);
  };

  const handleDownloadPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Build filter params - match exactly what's shown on screen
      const filters = {};
      
      // Important: Only send actual database IDs, not search terms
      // Since we can't determine member_id from search term, we'll need to pass filtered sale IDs
      // But backend doesn't support sale_ids filter, so we need different approach
      
      // For now, send the search term and let backend do best-effort filtering
      // Or better: Use the filtered sales list to extract unique calibers/members
      const filteredSalesList = sales.filter(sale => {
        const matchesSearch = sale.member_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          sale.caliber?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesTag = selectedTagFilter === 'ALLE' || (sale.tags && sale.tags.includes(selectedTagFilter));
        let matchesDate = true;
        if (dateFilter !== 'alle') {
          const { startDate, endDate } = getDateRange();
          const saleDate = new Date(sale.sold_at);
          if (startDate && endDate) {
            matchesDate = saleDate >= startDate && saleDate <= endDate;
          } else if (startDate) {
            matchesDate = saleDate >= startDate;
          } else if (endDate) {
            matchesDate = saleDate <= endDate;
          }
        }
        return matchesSearch && matchesTag && matchesDate;
      });
      
      // Extract sale IDs to send to backend
      filters.sale_ids = filteredSalesList.map(s => s.id);
      
      if (selectedTagFilter && selectedTagFilter !== 'ALLE') {
        filters.tag = selectedTagFilter;
      }
      
      // Add date range
      const { startDate, endDate } = getDateRange();
      if (startDate) {
        filters.start_date = startDate.toISOString().split('T')[0];
      }
      if (endDate) {
        filters.end_date = endDate.toISOString().split('T')[0];
      }
      
      // Call PDF endpoint
      const response = await axios.post(
        `${API_BASE}/api/admin/ammo-sales/generate-pdf`,
        filters,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ammo-salg-rapport-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF lastet ned!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Kunne ikke laste ned PDF');
    }
  };

  // Generate annual accounting report
  const handleDownloadAnnualReport = async () => {
    setGeneratingAnnualReport(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ammo-sales/generate-annual-report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          year: selectedYear
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate report');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ammo-arsrapport-regnskap-${selectedYear}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Årsrapport for ${selectedYear} lastet ned!`);
      setShowAnnualReportModal(false);
    } catch (error) {
      console.error('Error downloading annual report:', error);
      toast.error('Kunne ikke generere årsrapport');
    } finally {
      setGeneratingAnnualReport(false);
    }
  };

  // Generate available years for dropdown (current year back to 2020)
  const getAvailableYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 2020; year--) {
      years.push(year);
    }
    return years;
  };

  return (
    <div className="space-y-6">
      {/* Header with Action Buttons */}
      <div className="space-y-3">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex items-center gap-6">
            <div>
              <h3 className="text-lg font-semibold text-white">Salgshistorikk</h3>
              <p className="text-sm text-gray-400">
                {filteredSales.length} salg {selectedTagFilter !== 'ALLE' && `(filtrert: ${selectedTagFilter})`}
                {dateFilter !== 'alle' && (
                  <span className="ml-2 text-yellow-400">
                    • {dateFilter === 'siste_aktivitet' ? `Siste aktivitet ${lastActivityDate ? lastActivityDate.toLocaleDateString('nb-NO') : ''}` : 
                       dateFilter === 'idag' ? 'I dag' :
                       dateFilter === '1uke' ? 'Siste uke' :
                       dateFilter === '1mnd' ? 'Siste måned' :
                       dateFilter === '6mnd' ? 'Siste 6 mnd' :
                       dateFilter === '12mnd' ? 'Siste 12 mnd' :
                       dateFilter === 'egendefinert' ? 'Egendefinert' : dateFilter}
                  </span>
                )}
              </p>
            </div>
            
            {/* Filter toggle button - Only visible on mobile */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`md:hidden flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                showFilters || dateFilter !== 'alle'
                  ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
              </svg>
              Filtrer visning
              {dateFilter !== 'alle' && dateFilter !== 'siste_aktivitet' && (
                <span className="bg-yellow-500 text-gray-900 text-xs px-1.5 py-0.5 rounded-full">1</span>
              )}
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${showFilters ? 'rotate-180' : ''}`}>
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
          </div>
          
          {/* Action buttons - always visible */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleDownloadPDF}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" x2="12" y1="15" y2="3"></line>
              </svg>
              <span className="hidden sm:inline">Last ned PDF</span>
              <span className="sm:hidden">PDF</span>
            </button>
            
            <button
              onClick={() => setShowAnnualReportModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm"
              title="Generer årsrapport for regnskap"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
              </svg>
              <span className="hidden sm:inline">Årsrapport</span>
              <span className="sm:hidden">Rapport</span>
            </button>
            
            <button
              onClick={handleRegisterSale}
              className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500 hover:bg-yellow-400 text-gray-900 rounded-lg transition-colors font-medium text-sm"
              title="Registrer ammunisjonssalg"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" x2="12" y1="5" y2="19"></line>
                <line x1="5" x2="19" y1="12" y2="12"></line>
              </svg>
              <span className="hidden sm:inline">Registrer salg</span>
              <span className="sm:hidden">+ Salg</span>
            </button>
          </div>
        </div>
        
        {/* Filter Section - Always visible on desktop (md+), collapsible on mobile */}
        <div className={`${showFilters ? 'block' : 'hidden'} md:block bg-gray-700/30 rounded-lg p-4 border border-gray-700`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-400 mr-2">Periode:</span>
            
            {/* Quick filter buttons */}
            {[
              { key: 'alle', label: 'Alle' },
              { key: 'siste_aktivitet', label: 'Siste aktivitet', tooltip: lastActivityDate ? `${lastActivityDate.toLocaleDateString('nb-NO')}` : 'Ingen aktivitet funnet' },
              { key: 'idag', label: 'I dag' },
              { key: '1uke', label: '1 uke' },
              { key: '1mnd', label: '1 mnd' },
              { key: '6mnd', label: '6 mnd' },
              { key: '12mnd', label: '12 mnd' },
              { key: 'egendefinert', label: 'Egendefinert' },
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => setDateFilter(filter.key)}
                title={filter.tooltip}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                  dateFilter === filter.key
                    ? 'bg-yellow-500 text-gray-900'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {filter.label}
              </button>
            ))}
            
            {/* Clear filter button */}
            {dateFilter !== 'alle' && (
              <button
                onClick={() => setDateFilter('alle')}
                className="px-2 py-1.5 text-gray-400 hover:text-white transition-colors"
                title="Fjern filter"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
          
          {/* Custom Date Range Inputs */}
          {dateFilter === 'egendefinert' && (
            <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-gray-600">
              <label className="flex items-center gap-2">
                <span className="text-sm text-gray-300">Fra:</span>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                />
              </label>
              <label className="flex items-center gap-2">
                <span className="text-sm text-gray-300">Til:</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                />
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Sales list grouped by date */}
      {Object.keys(salesByDate).length === 0 ? (
        <div className="text-center py-12">
          <img src="/patron-symbol.svg" alt="Ammo" className="h-16 w-16 mx-auto opacity-20 mb-4" />
          <p className="text-gray-400">Ingen ammo-salg registrert ennå</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(salesByDate)
            .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
            .map(([date, dateSales]) => (
              <div key={date} className="space-y-3">
                <div className="flex items-center gap-2 text-gray-400">
                  <Calendar className="h-4 w-4" />
                  <h3 className="font-medium">{date}</h3>
                  <span className="text-sm">({dateSales.length} salg)</span>
                </div>
                
                <div className="space-y-2">
                  {dateSales.map((sale) => (
                    <div
                      key={sale.id}
                      className="p-4 rounded-lg flex items-center justify-between bg-gray-700"
                    >
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                          <div className="flex items-center gap-3 flex-wrap">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-white font-medium">{sale.member_name}</span>
                            <span className="text-gray-500">•</span>
                            <span className="text-gray-400 text-sm">
                              {new Date(sale.sold_at).toLocaleTimeString('nb-NO', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          {!sale.is_external && (
                            sale.member_birth_number ? (
                              <span className="text-xs text-gray-300 font-mono px-2 py-0.5 bg-gray-800 rounded" title="Fødselsdato fra profilen">
                                📅 {sale.member_birth_number}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-500 italic" title="Be medlemmet legge til sin fødselsdato under Profil">
                                Fødselsdato ikke lagt inn. Oppfordre til å gjøre dette
                              </span>
                            )
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                          <div>
                            <span className="text-gray-400">Kaliber:</span>
                            <p className="font-medium text-white">
                              {sale.caliber}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-400">Esker:</span>
                            <p className="font-medium text-white">
                              {sale.boxes_sold} stk
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-400">Skudd:</span>
                            <p className="font-medium text-white">
                              {sale.total_rounds} stk
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-400">Beløp:</span>
                            <p className={`font-medium ${isNonRevenue(sale.tags, nonRevenueTags) ? 'text-gray-400' : 'text-yellow-400'}`}>
                              {isNonRevenue(sale.tags, nonRevenueTags) ? '0' : sale.total_amount?.toFixed(2) || '0'} kr
                              {isNonRevenue(sale.tags, nonRevenueTags) && (
                                <span className="text-xs text-orange-400 ml-1">(intern)</span>
                              )}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-400">Kategori:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {sale.tags && sale.tags.length > 0 ? (
                                sale.tags.map((tag, idx) => (
                                  <span key={idx} className={`px-2 py-0.5 text-xs rounded ${getTagColor(tag)}`}>
                                    {tag}
                                  </span>
                                ))
                              ) : (
                                <span className="text-gray-500 text-xs">-</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        {/* Comment icon - always reserve space */}
                        <div className="relative">
                          {sale.comment ? (
                            <>
                              <button
                                onMouseEnter={() => setHoveredComment(sale.id)}
                                onMouseLeave={() => setHoveredComment(null)}
                                onClick={() => handleEditClick(sale)}
                                className="p-2 text-cyan-400 hover:text-cyan-300 bg-cyan-900/30 rounded transition-colors border border-cyan-500/30 relative"
                                title="Klikk for å se/redigere kommentar"
                              >
                                <MessageSquare className="h-5 w-5 fill-current" />
                              </button>
                              
                              {/* Hover Popup */}
                              {hoveredComment === sale.id && (
                                <div className="absolute z-50 right-0 mt-2 w-64 p-3 bg-gray-900 border border-cyan-500/50 rounded-lg shadow-xl">
                                  <div className="flex items-start gap-2 mb-2">
                                    <MessageSquare className="h-4 w-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                                    <p className="text-xs font-semibold text-cyan-400">Intern kommentar:</p>
                                  </div>
                                  <p className="text-sm text-gray-300 whitespace-pre-wrap break-words">
                                    {sale.comment}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-2 italic">Klikk ikonet for å redigere</p>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="p-2 w-9 h-9"></div>
                          )}
                        </div>
                        
                        <button
                          onClick={() => handleEditClick(sale)}
                          className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/20 rounded transition-colors"
                          title="Rediger salg"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(sale.id)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded transition-colors"
                          title="Slett salg"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingSale && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-lg w-full max-w-md p-6 my-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Rediger salg</h3>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Medlem
                </label>
                <p className="text-white bg-gray-700 px-3 py-2 rounded">
                  {editingSale.member_name}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Kaliber
                </label>
                <p className="text-white bg-gray-700 px-3 py-2 rounded">
                  {editingSale.caliber}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Salgsdato
                </label>
                <input
                  type="date"
                  value={editForm.sale_date}
                  onChange={(e) => setEditForm({ ...editForm, sale_date: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  max={new Date().toISOString().split('T')[0]}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Endre datoen hvis salget gjelder en annen treningsdag
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Antall esker
                </label>
                {/* Mobile-friendly +/- buttons */}
                <div className="flex items-center gap-4 justify-center">
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, boxes_sold: Math.max(1, editForm.boxes_sold - 1) })}
                    className="w-16 h-16 bg-gray-700 hover:bg-gray-600 border-2 border-gray-600 hover:border-red-500 rounded-lg flex items-center justify-center text-white transition-all active:scale-95 touch-manipulation"
                    disabled={editForm.boxes_sold <= 1}
                  >
                    <Minus className="h-8 w-8" />
                  </button>
                  
                  <div className="flex-1 text-center">
                    <input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      value={editForm.boxes_sold}
                      onChange={(e) => {
                        const cleaned = (e.target.value || '').replace(/^0+(?=\d)/, '');
                        if (cleaned === '') {
                          setEditForm({ ...editForm, boxes_sold: 0 });
                          return;
                        }
                        let num = parseInt(cleaned, 10);
                        if (isNaN(num) || num < 0) num = 0;
                        setEditForm({ ...editForm, boxes_sold: num });
                      }}
                      onFocus={(e) => e.target.select()}
                      className="w-full bg-transparent text-4xl font-bold text-white text-center outline-none focus:ring-2 focus:ring-yellow-500 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      data-testid="ammo-edit-boxes-input"
                      aria-label="Antall esker"
                    />
                    <div className="text-sm text-gray-400 mt-1">
                      esker
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, boxes_sold: editForm.boxes_sold + 1 })}
                    className="w-16 h-16 bg-gray-700 hover:bg-gray-600 border-2 border-gray-600 hover:border-green-500 rounded-lg flex items-center justify-center text-white transition-all active:scale-95 touch-manipulation"
                  >
                    <Plus className="h-8 w-8" />
                  </button>
                </div>
              </div>
              
              {/* Tags Section */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Kategori
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {availableTags.map((tag) => (
                    <label
                      key={tag}
                      className="flex items-center space-x-2 cursor-pointer p-2 rounded bg-gray-700 hover:bg-gray-600"
                    >
                      <input
                        type="radio"
                        name="editSaleCategory"
                        checked={editForm.tags.includes(tag)}
                        onChange={() => {
                          setEditForm({ ...editForm, tags: [tag] });
                        }}
                        className="text-yellow-500 focus:ring-yellow-500"
                      />
                      <span className="text-sm text-white">{tag}</span>
                    </label>
                  ))}
                </div>
                {editForm.tags.length > 0 && (
                  <p className="text-xs text-gray-400 mt-2">
                    Valgt kategori: {editForm.tags[0]}
                  </p>
                )}
                
                {/* Add new category */}
                <div className="mt-4 pt-4 border-t border-gray-600">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Legg til ny kategori
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCategoryInput}
                      onChange={(e) => setNewCategoryInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddNewCategory();
                        }
                      }}
                      placeholder="Skriv ny kategori..."
                      className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm placeholder-gray-400"
                      disabled={savingNewCategory}
                    />
                    <button
                      type="button"
                      onClick={handleAddNewCategory}
                      disabled={savingNewCategory || !newCategoryInput.trim()}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded text-sm font-medium transition-colors flex items-center gap-1"
                    >
                      <Plus className="h-4 w-4" />
                      Legg til
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Nye kategorier lagres automatisk og blir tilgjengelige for alle salg
                  </p>
                </div>
              </div>
              
              {/* Comment Section */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Intern kommentar (kun for skyteledere)
                </label>
                <textarea
                  value={editForm.comment}
                  onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })}
                  placeholder="Valgfri intern kommentar om dette salget..."
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm resize-none"
                  rows="3"
                  maxLength="500"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {editForm.comment.length}/500 tegn
                </p>
              </div>
              
              <div className="bg-gray-700 p-3 rounded">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">Totalt skudd:</span>
                  <span className="text-white font-medium">
                    {(editForm.boxes_sold * (editingSale.total_rounds / editingSale.boxes_sold)).toFixed(0)} stk
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Totalt beløp:</span>
                  <span className="text-yellow-400 font-medium">
                    {(editForm.boxes_sold * (editingSale.total_amount / editingSale.boxes_sold)).toFixed(2)} kr
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setEditingSale(null)}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleUpdateSale}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
              >
                Lagre endringer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Log Section - Same style as KLUBB-LOGG */}
      <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              // Always reload history when opening (not just first time)
              if (!showDeletionLog) {
                loadDeletedSales();
              }
              setShowDeletionLog(!showDeletionLog);
            }}
            className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
          >
            <h3 className="font-medium text-sm">Vis historikk endringer utført</h3>
            <svg 
              className={`h-4 w-4 transition-transform ${showDeletionLog ? 'rotate-180' : ''}`}
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        
        {showDeletionLog && (
          <div className="mt-3 border-t border-gray-600 pt-3">
            {loadingDeletedSales ? (
              <div className="text-center py-4">
                <div className="text-gray-400 text-sm">Henter historikk...</div>
              </div>
            ) : deletedSales.length === 0 ? (
              <div className="text-center py-4">
                <div className="text-gray-400 text-sm">Ingen endringer eller slettinger funnet</div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-gray-400 mb-3">
                  Viser {deletedSales.length} historikkoppføringer (endringer og slettinger)
                </div>
                
                {/* History list */}
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {deletedSales.map((sale) => {
                    const isDeleted = sale.deleted === true;
                    const hasEdits = sale.edit_history && sale.edit_history.length > 0;
                    
                    return (
                      <div 
                        key={sale.id} 
                        className="bg-gray-700/50 border border-gray-600 rounded p-3 text-sm"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            {/* Sale details */}
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-white font-medium">
                                {sale.member_name || 'Ukjent medlem'}
                              </span>
                              <span className="text-gray-400">•</span>
                              <span className="text-gray-300">
                                {sale.caliber}
                              </span>
                              <span className="text-gray-400">•</span>
                              <span className="text-gray-300">
                                {sale.boxes_sold} esker
                              </span>
                              <span className="text-gray-400">•</span>
                              <span className={`font-medium ${isNonRevenue(sale.tags, nonRevenueTags) ? 'text-gray-400' : 'text-yellow-400'}`}>
                                {isNonRevenue(sale.tags, nonRevenueTags) ? '0.00' : sale.total_amount?.toFixed(2)} kr
                              </span>
                              {isNonRevenue(sale.tags, nonRevenueTags) && (
                                <span className="text-xs text-orange-400">(intern)</span>
                              )}
                              {isDeleted && (
                                <>
                                  <span className="text-gray-400">•</span>
                                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded border border-red-500/30">
                                    SLETTET
                                  </span>
                                </>
                              )}
                            </div>
                            
                            {/* Deletion info */}
                            {isDeleted && (
                              <div className="text-xs text-red-300 mb-2">
                                Slettet av <strong>{sale.deleted_by_name || sale.deleted_by_email || 'Ukjent'}</strong> den{' '}
                                {new Date(sale.deleted_at).toLocaleDateString('no-NO', {
                                  year: 'numeric',
                                  month: 'long', 
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            )}
                            
                            {/* Edit history */}
                            {hasEdits && (
                              <div className={`${isDeleted ? 'mt-2 pt-2 border-t border-gray-600' : ''}`}>
                                <div className="text-xs text-blue-300 mb-1">
                                  {isDeleted ? 'Tidligere endringer før sletting:' : 'Endringer:'}
                                </div>
                                {sale.edit_history.slice(-5).reverse().map((edit, idx) => (
                                  <div key={idx} className="text-xs text-gray-300 ml-2 mb-1">
                                    • <span className="text-gray-400">
                                      {new Date(edit.timestamp).toLocaleString('no-NO', { 
                                        day: '2-digit', 
                                        month: '2-digit', 
                                        year: 'numeric',
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                      })}
                                    </span>
                                    {' '}- <strong>{edit.edited_by_name}</strong>: {edit.changes.join(', ')}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          {/* Restore button (only for deleted sales) */}
                          {isDeleted && (
                            <button
                              onClick={() => {
                                if (window.confirm(`Er du sikker på at du vil gjenopprette salget for ${sale.member_name || 'ukjent medlem'}?`)) {
                                  restoreSale(sale.id);
                                }
                              }}
                              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors whitespace-nowrap"
                              title="Gjenopprett dette salget"
                            >
                              Angre sletting
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Annual Report Modal */}
      {showAnnualReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md relative">
            {/* Close button */}
            <button
              onClick={() => setShowAnnualReportModal(false)}
              className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded-full transition-colors"
              aria-label="Lukk"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <h3 className="text-lg font-semibold text-white mb-2">Rapport til regnskap</h3>
            <p className="text-sm text-gray-400 mb-4">
              Generer en årsrapport med månedlige totaler per ammunisjonstype. 
              Perfekt for regnskapsformål.
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Velg år
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              >
                {getAvailableYears().map(year => (
                  <option key={year} value={year}>
                    {year} {year === new Date().getFullYear() - 1 ? '(fjoråret)' : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Rapporten viser perioden 01.01.{selectedYear} - 31.12.{selectedYear}
              </p>
            </div>
            
            <div className="bg-gray-700/50 rounded-lg p-4 mb-6">
              <h4 className="text-sm font-medium text-yellow-400 mb-2">Rapporten inneholder:</h4>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>• Årsoversikt per ammunisjonstype</li>
                <li>• Totalt antall esker og skudd</li>
                <li>• Total omsetning per type</li>
                <li>• Lagerstatus ved årsslutt</li>
                <li>• Månedsoversikt med omsetning</li>
              </ul>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowAnnualReportModal(false)}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleDownloadAnnualReport}
                disabled={generatingAnnualReport}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {generatingAnnualReport ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Genererer...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" x2="12" y1="15" y2="3"></line>
                    </svg>
                    Last ned rapport
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmmoSalesList;
